import { bannerText, type Schedule } from './schedule';
import { formatMoneyPence, titleCaseStatus } from './format';
import { isFinancialAuditEntry, jobStateLabel, type JobAuditLog } from '../types/models';

function record(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch { return null; }
  }
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : null;
}

export function assignmentName(value: unknown): string {
  if (value == null || value === '') return 'Unassigned';
  const data = record(value);
  if (!data) return 'Name not recorded';
  const name = data.assignmentType === 'subcontractor'
    ? data.assignedSubcontractorName : data.assignedUserDisplayName ?? data.assignedSubcontractorName;
  if (typeof name === 'string' && name.trim()) return name.trim();
  const keys = ['assignmentType', 'assignedUserId', 'assignedSubcontractorId', 'assignedUserDisplayName', 'assignedSubcontractorName'];
  if (keys.some(key => key in data) && keys.every(key => data[key] == null || data[key] === '')) return 'Unassigned';
  return 'Name not recorded';
}

export function assignmentChangeDetails(oldValue: unknown, newValue: unknown): string {
  return `From ${assignmentName(oldValue)} to ${assignmentName(newValue)}`;
}

export function readableHistoryField(field: string | null | undefined): string {
  if (!field) return 'Field';
  const names: Record<string, string> = {
    registrationnumber: 'Registration', fueltype: 'Fuel', vehiclemileage: 'Vehicle mileage',
    pickuplocation: 'Collect from', dropofflocation: 'Deliver to', customername: 'Customer',
    courtesycarrequired: 'Courtesy car required', courtesycarregistration: 'Courtesy car registration',
    courtesycarmileage: 'Courtesy car mileage', clientcreatedat: 'Reported creation time',
    state: 'Status', status: 'Status', accountingstatus: 'Accounting status', pricing: 'Price',
    scheduledfor: 'Booked for', scheduleduntil: 'Booked until', scheduleddateonly: 'Any time',
    invoicereference: 'Invoice reference', invoicedat: 'Invoiced at',
    assigneduserid: 'Assigned staff member', assigneduserdisplayname: 'Assigned staff member',
    assignedsubcontractorid: 'Assigned subcontractor', assignedsubcontractorname: 'Assigned subcontractor',
    assignmenttype: 'Assignment type', imagesessions: 'Images', notes: 'Notes',
  };
  return names[field.toLowerCase()] ?? field.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').replace(/^./, x => x.toUpperCase());
}

export function readableHistoryValue(fieldChanged: string | null | undefined, value: unknown): string {
  const field = (fieldChanged ?? '').toLowerCase();
  if (field === 'assignment') return assignmentName(value);
  if (/^(collection|delivery)contact/.test(field) || field === 'contacts') return '[contact details]';
  if (value == null || value === '') return field.startsWith('scheduled') ? 'Not set' : '(empty)';
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  if (/image|thumbnail|storagepath|checksum/.test(field)
    || /firebasestorage\.googleapis\.com|storage\.googleapis\.com|[?&]token=|X-Goog-Signature/i.test(text)) return '[secure evidence file]';
  if (/assigned.*id$|^userid$/.test(field)) return 'Name not recorded';
  if (field === 'state' || field === 'status') return jobStateLabel(String(value));
  if (field === 'accountingstatus') return titleCaseStatus(String(value));
  if (field === 'scheduledfor' || field === 'scheduleduntil') {
    const date = value instanceof Date ? value : new Date(String(value));
    if (!Number.isFinite(date.getTime())) return 'Date unavailable';
    return bannerText({ start: date, end: null, dateOnly: false } satisfies Schedule) ?? 'Not set';
  }
  if (field === 'scheduleddateonly' || field === 'courtesycarrequired') {
    return value === true || value === 'true' ? 'Yes' : 'No';
  }
  if (field === 'pricing' && typeof value === 'object') {
    const pricing = record(value);
    if (typeof pricing?.amountPence === 'number' && Number.isFinite(pricing.amountPence)) {
      try { return formatMoneyPence(pricing.amountPence, typeof pricing.currency === 'string' ? pricing.currency : 'GBP'); }
      catch { return 'Price recorded'; }
    }
  }
  return typeof value === 'object' ? 'Details recorded' : text;
}

function eventKey(log: JobAuditLog): string | null {
  const time = log.timestamp?.toMillis?.();
  return Number.isFinite(time) ? JSON.stringify([time, log.jobId, log.userId, log.actionType]) : null;
}

/** Finance entries replace only their matching public marker; distinct events
 * remain separate. Unknown timestamps cannot safely be treated as duplicates. */
export function mergeJobHistory(ordinary: JobAuditLog[], financial: JobAuditLog[]): JobAuditLog[] {
  const financeKeys = new Set(financial.map(eventKey).filter((key): key is string => key !== null));
  const entries = [
    ...ordinary.filter(log => !isFinancialAuditEntry(log) || !financeKeys.has(eventKey(log) ?? ''))
      .map(log => ({ ...log, id: `job/${log.id}` })),
    ...financial.map(log => ({ ...log, id: `finance/${log.id}` })),
  ];
  return entries.filter(log => !(log.fieldChanged === 'scheduledDateOnly'
    && (log.oldValue == null || log.oldValue === false || log.oldValue === 'false')
    && (log.newValue == null || log.newValue === false || log.newValue === 'false')))
    .sort((a, b) => (a.timestamp?.toMillis?.() ?? 0) - (b.timestamp?.toMillis?.() ?? 0));
}
